import { InvoiceFileProcessingStatusDto } from 'src/generated/services/TFinancialApi';
import { IGroupingItemData } from './types';
import { processingStatuses, invalidFileStatuses } from './configs';

/**
 * Utility functions for upload data processing and filtering
 */

/**
 * Checks if a status item belongs to "Other" category
 * Includes:
 * - Invalid file statuses (not an invoice, missing fields, duplicate, etc.)
 * - Error statuses (OCR error, transform error, upload failed, etc.)
 * - Marked for review statuses
 * - Under review files
 */
export function isStatusOther(statusItem: InvoiceFileProcessingStatusDto | null): boolean {
  if (!statusItem) return false;

  const errorStatuses = [
    'OCR_ERROR',
    'TRANSFORM_ERROR',
    'MESSAGE_PROCESSING_FAILED',
    'UPLOAD_FROM_EMAIL_FAILED',
    'UPLOAD_FROM_S3_FAILED',
    'PIPELINE_TRIGGER_FAILED',
  ];

  const markedForReviewStatuses = ['MARKED_MISSING_REQUIRED_FIELD_FOR_REVIEW', 'MARKED_INCONSISTENT_DATA_FOR_REVIEW'];

  // Check if it's an invalid file status (uses existing config)
  if (invalidFileStatuses.includes(statusItem?.status)) {
    return true;
  }

  // Check if it's an error status
  if (errorStatuses.includes(statusItem?.status)) {
    return true;
  }

  // Check if it's marked for review
  if (markedForReviewStatuses.includes(statusItem?.status)) {
    return true;
  }

  // Include files that are under review
  const hasNoInvoiceStatus = !statusItem?.invoiceStatus;
  const isInReviewStatus = statusItem?.fileResolutionStatus && ['REVIEW_REQUIRED', 'UNDER_REVIEW'].includes(statusItem?.fileResolutionStatus);

  return hasNoInvoiceStatus && isInReviewStatus;
}

/**
 * Checks if a status item is under review
 */
export function isStatusUnderReview(statusItem: InvoiceFileProcessingStatusDto | null): boolean {
  if (!statusItem) return false;

  const hasNoInvoiceStatus = !statusItem?.invoiceStatus;
  const isInReviewStatus = statusItem?.fileResolutionStatus && ['REVIEW_REQUIRED', 'UNDER_REVIEW'].includes(statusItem?.fileResolutionStatus);

  return hasNoInvoiceStatus && isInReviewStatus;
}

/**
 * Checks if a status item is processing (but not other or under review)
 */
export function isStatusProcessing(statusItem: InvoiceFileProcessingStatusDto | null, errorProcessingStatuses: string[] = []): boolean {
  if (!statusItem) return false;

  const isOther = isStatusOther(statusItem);
  const isUnderReview = isStatusUnderReview(statusItem);

  return processingStatuses.includes(statusItem?.status) && !errorProcessingStatuses.includes(statusItem?.status) && !isOther && !isUnderReview;
}

/**
 * Filters upload results into categorized lists
 */
export function categorizeUploadResults(
  uploadResults: IGroupingItemData[],
  errorProcessingStatuses: string[]
): {
  processing: IGroupingItemData[];
  other: IGroupingItemData[];
  underReview: IGroupingItemData[];
  completed: { [key: string]: IGroupingItemData[] };
} {
  const processing: IGroupingItemData[] = [];
  const other: IGroupingItemData[] = [];
  const underReview: IGroupingItemData[] = [];
  const completed: { [key: string]: IGroupingItemData[] } = {};

  uploadResults?.forEach((result: IGroupingItemData) => {
    const invoiceDetail = result.invoiceDetail;
    if (invoiceDetail) {
      const serviceProviderCode = invoiceDetail?.serviceProviderCode;
      const status = invoiceDetail?.status;
      const isProcessingStatus = isStatusProcessing(invoiceDetail, errorProcessingStatuses);
      const isOtherStatus = isStatusOther(invoiceDetail);
      const isUnderReviewStatus = isStatusUnderReview(invoiceDetail);

      // Priority 1: Processing
      if (isProcessingStatus) {
        processing.push(result);
      }
      // Priority 2: Items with service provider go to completed sections
      else if (serviceProviderCode) {
        completed[serviceProviderCode] = completed[serviceProviderCode] || [];
        completed[serviceProviderCode].push(result);
      }
      // Priority 3: Other section (resolution issues + error statuses + under review)
      else if (isOtherStatus) {
        other.push(result);
      }
      // Priority 4: Under Review (deprecated - now included in Other, kept for backward compatibility)
      else if (isUnderReviewStatus) {
        underReview.push(result);
      }
    }
  });

  return {
    processing,
    other,
    underReview,
    completed,
  };
}
